<template lang="html">
  <div
    class="product-gallery flex flex-col gap-2 sm:flex-row sm:items-stretch sm:gap-3"
  >
    <div
      v-if="totalSlides > 1"
      class="hidden shrink-0 flex-col gap-2 overflow-y-auto sm:flex sm:max-h-full sm:min-h-0 sm:w-18"
      role="tablist"
      aria-label="Product media"
    >
      <button
        v-for="(img, index) in slides"
        :key="`thumb-desktop-${img.id ?? index}`"
        type="button"
        role="tab"
        :aria-selected="index === activeIndex"
        :aria-label="`View image ${index + 1}`"
        class="size-18 shrink-0 overflow-hidden rounded-sm transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
        :class="thumbClasses(index)"
        @click="selectSlide(index)"
      >
        <UiPicture
          :src="img.id"
          variant="imageFull"
          :alt="galleryAlt(index)"
          sizes="72px"
          :loading="index === activeIndex ? undefined : 'lazy'"
          class="size-full"
        />
      </button>
      <button
        v-for="(video, vIndex) in videos"
        :key="`thumb-desktop-video-${video.id}`"
        type="button"
        role="tab"
        :aria-selected="slides.length + vIndex === activeIndex"
        :aria-label="videoAlt(video)"
        class="relative size-18 shrink-0 overflow-hidden rounded-sm transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
        :class="thumbClasses(slides.length + vIndex)"
        @click="selectSlide(slides.length + vIndex)"
      >
        <UiPicture
          v-if="video.posterId"
          :src="video.posterId"
          variant="imageFull"
          :alt="videoAlt(video)"
          sizes="72px"
          loading="lazy"
          class="size-full"
        />
        <div
          v-else
          class="flex size-full items-center justify-center bg-black text-white"
        >
          <UIcon name="i-lucide-play" class="size-5" />
        </div>
        <span
          class="pointer-events-none absolute inset-0 flex items-center justify-center bg-black/35"
        >
          <UIcon name="i-lucide-play" class="size-4 text-white" />
        </span>
      </button>
    </div>

    <div class="relative min-w-0 flex-1">
      <ClientOnly>
        <template #fallback>
          <div class="hidden" />
        </template>
        <LightgalleryContainer
          v-if="needShowGallery"
          key="gallery"
          :onInit="handleInitGallery"
        >
          <a v-for="img in slides" :key="img.id" :data-src="img.src" />
        </LightgalleryContainer>
      </ClientOnly>

      <!--
        Aspect box in normal flow (not absolute Swiper): reserves square height
        for CLS. flex-1 parent always has width even when thumbs are absent.
      -->
      <div class="relative aspect-square w-full overflow-hidden bg-elevated">
        <SwiperContainer
          loop
          :slides-per-view="1"
          class="!h-full w-full"
          @swiper="handleSwiperInited"
          @slide-change="handleSlideChange"
        >
          <SwiperSlide
            v-for="(img, index) in slides"
            :key="img.id"
            class="!h-full"
          >
            <UiPicture
              :src="img.id"
              variant="imageFull"
              class="size-full"
              :alt="galleryAlt(index)"
              sizes="100vw sm:50vw"
              :width="640"
              :height="640"
              :loading="index === 0 ? undefined : 'lazy'"
              :fetchpriority="index === 0 ? 'high' : undefined"
              @click="openGallery()"
            />
          </SwiperSlide>
          <!-- Видео после фото: первое изображение остаётся главным для
          листингов, OG и фидов -->
          <SwiperSlide
            v-for="video in videos"
            :key="video.id"
            v-slot="{ isActive }"
            class="!h-full"
          >
            <ProductVideo
              :key="isBelowLg ? 'autoplay' : 'hover'"
              :src="video.src"
              :poster="video.posterUrl"
              :label="videoAlt(video)"
              :active="isActive"
              :autoplay="isBelowLg"
            />
          </SwiperSlide>
        </SwiperContainer>

        <div
          v-if="totalSlides > 1"
          key="prevSlide"
          class="product-gallery__nav absolute top-1/2 left-2 z-10 -translate-y-1/2 sm:left-3"
        >
          <button
            type="button"
            aria-label="Предыдущее медиа"
            class="flex size-10 items-center justify-center rounded-full border border-default/60 bg-default/90 text-highlighted shadow-md backdrop-blur-sm transition-[background-color,box-shadow,transform] hover:bg-default hover:shadow-lg active:scale-95 sm:size-11"
            @click="$slider?.slidePrev()"
          >
            <UIcon
              name="i-lucide-chevron-left"
              class="size-5 sm:size-6"
            />
          </button>
        </div>
        <div
          v-if="totalSlides > 1"
          key="nextSlide"
          class="product-gallery__nav absolute top-1/2 right-2 z-10 -translate-y-1/2 sm:right-3"
        >
          <button
            type="button"
            aria-label="Следующее медиа"
            class="flex size-10 items-center justify-center rounded-full border border-default/60 bg-default/90 text-highlighted shadow-md backdrop-blur-sm transition-[background-color,box-shadow,transform] hover:bg-default hover:shadow-lg active:scale-95 sm:size-11"
            @click="$slider?.slideNext()"
          >
            <UIcon
              name="i-lucide-chevron-right"
              class="size-5 sm:size-6"
            />
          </button>
        </div>
      </div>

      <div
        v-if="totalSlides > 1"
        class="mt-2 flex gap-2 overflow-x-auto pb-1 sm:hidden"
        role="tablist"
        aria-label="Product media"
      >
        <button
          v-for="(img, index) in slides"
          :key="`thumb-mobile-${img.id ?? index}`"
          type="button"
          role="tab"
          :aria-selected="index === activeIndex"
          :aria-label="`View image ${index + 1}`"
          class="size-18 shrink-0 overflow-hidden rounded-sm transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
          :class="thumbClasses(index)"
          @click="selectSlide(index)"
        >
          <UiPicture
            :src="img.id"
            variant="imageFull"
            :alt="galleryAlt(index)"
            sizes="72px"
            :loading="index === activeIndex ? undefined : 'lazy'"
            class="size-full"
          />
        </button>
        <button
          v-for="(video, vIndex) in videos"
          :key="`thumb-mobile-video-${video.id}`"
          type="button"
          role="tab"
          :aria-selected="slides.length + vIndex === activeIndex"
          :aria-label="videoAlt(video)"
          class="relative size-18 shrink-0 overflow-hidden rounded-sm transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
          :class="thumbClasses(slides.length + vIndex)"
          @click="selectSlide(slides.length + vIndex)"
        >
          <UiPicture
            v-if="video.posterId"
            :src="video.posterId"
            variant="imageFull"
            :alt="videoAlt(video)"
            sizes="72px"
            loading="lazy"
            class="size-full"
          />
          <div
            v-else
            class="flex size-full items-center justify-center bg-black text-white"
          >
            <UIcon name="i-lucide-play" class="size-5" />
          </div>
          <span
            class="pointer-events-none absolute inset-0 flex items-center justify-center bg-black/35"
          >
            <UIcon name="i-lucide-play" class="size-4 text-white" />
          </span>
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { PromiseController } from '@repo/alt/common/Utils/Async.js'
import type { ProductDto } from '@repo/api/web/models'
import type { LightGallery } from 'lightgallery/lightgallery'
import type Swiper from 'swiper'
import { Swiper as SwiperContainer, SwiperSlide } from 'swiper/vue'
import 'lightgallery/css/lightgallery.css'
import 'swiper/swiper-bundle.css'

import ProductVideo from './ProductVideo.vue'

import UiPicture from '~/app/components/Image/UiPicture.vue'

const LightgalleryContainer = defineAsyncComponent(
  () => import('lightgallery/vue'),
)

const props = withDefaults(
  defineProps<{
    value: ProductDto
  }>(),
  {},
)

const initGalleryPromise = new PromiseController()
const { support } = useResources()
const { isBelowLg } = useDevice()
const $slider = ref<Swiper>()
const $gallery = ref<LightGallery>()
const needShowGallery = ref(false)
const activeIndex = ref(0)

const slides = computed(() => {
  const images = props.value.images ?? []
  const items = images.map(img => ({
    id: img.id,
    src: support.getResourceUrl(img.id!),
  }))

  if (items.length === 0) {
    return [{ id: undefined, src: '/Plug.svg' }]
  }

  return items
})

/**
 * Видео товара. Ролик в слайдере, в lightgallery не попадает:
 * галерея остаётся про фото, а плеер платформы туда тянуть незачем.
 */
const videos = computed(() =>
  (props.value.videos ?? []).map(video => ({
    displayName: video.displayName,
    id: video.id,
    posterId: video.posterResourceId ?? undefined,
    posterUrl: video.posterResourceId
      ? support.getResourceUrl(video.posterResourceId)
      : undefined,
    src: support.getResourceUrl(video.id),
  })),
)

/** Стрелки и превью нужны, когда слайд не один — считаем и фото, и видео */
const totalSlides = computed(() => slides.value.length + videos.value.length)

function videoAlt(video: { displayName?: string | null }) {
  const name = props.value.wellKnownAttributes?.Name ?? ''
  return video.displayName?.trim() || `${name} — видео`
}

function galleryAlt(index: number) {
  const name = props.value.wellKnownAttributes?.Name ?? ''

  return index === 0 ? name : `${name} — фото ${index + 1}`
}

function thumbClasses(index: number) {
  return index === activeIndex.value
    ? 'border-2 border-primary'
    : 'border-2 border-default'
}

function handleSwiperInited(swiper: Swiper) {
  $slider.value = swiper
  activeIndex.value = swiper.realIndex
}

function handleSlideChange(swiper: Swiper) {
  activeIndex.value = swiper.realIndex
}

function selectSlide(index: number) {
  $slider.value?.slideToLoop(index)
}

async function openGallery() {
  needShowGallery.value = true

  if (!$gallery.value) {
    await initGalleryPromise.promise
  }

  $gallery.value?.openGallery()
}

function handleInitGallery(gallery: { instance: LightGallery }) {
  $gallery.value = gallery.instance
  initGalleryPromise.resolve($gallery.value)
}
</script>
